import MeridianColumn from '@amzn/meridian/column';
import styled from '@emotion/styled';
import React, { useMemo, useState, useEffect } from 'react';
import { IAsynchronouslyLoadedValue } from '../../redux/types';
import { ShipmentTrackingDetail, TrackingMilestone } from '../../redux/types/trackingDetails';
import { color } from '../../theme/colors';
import { useTranslation } from '../blocks';
import { SuperPaginatedTable, SuperPaginatedTableColumn } from '../constructed/SuperPaginatedTable';

export type ReturnOrderTrackingTableProps = {
    trackingDetails: IAsynchronouslyLoadedValue<ShipmentTrackingDetail[]>;
};

interface TrackingItem {
    id: string;
    carrierName: string;
    carrierTrackingNumber: string;
    status: string;
    estimatedDeliveryDate: string;
    milestones: TrackingMilestone[];
    isExpanded: boolean;
}

interface MilestoneItem {
    id: string;
    parentId: string;
    carrierTrackingNumber: string;
    status: string;
    eventTime: string;
    eventLocation: string;
    isMilestone: true;
}

const INDENTATION_STYLE = { paddingLeft: 20, color: color.gray[600] };
const EXPAND_ICON = '▶ ';
const COLLAPSE_ICON = '▼ ';

const MilestoneField = ({ label, value }: { label: string; value: string }) => (
    <span style={INDENTATION_STYLE}>
        {label}
        {': '}
        {value}
    </span>
);

const BorderedColumn = styled(MeridianColumn)`
    border: 1px solid ${color.gray[300]};
    box-shadow: 0px 0px 3px ${color.gray[300]};
`;

const ReturnOrderTrackingTable = ({ trackingDetails }: ReturnOrderTrackingTableProps) => {
    const { t } = useTranslation('returnOrderTrackingTable');
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const UNKNOWN_VALUE = t('unknownValue-label');
    useEffect(() => {
        setExpandedRows(new Set());
    }, [trackingDetails]);

    const toggleRow = (rowId: string) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(rowId)) {
            newExpanded.delete(rowId);
        } else {
            newExpanded.add(rowId);
        }

        setExpandedRows(newExpanded);
    };

    const formattedTrackingData = useMemo((): (TrackingItem | MilestoneItem)[] => {
        if (trackingDetails.status === 'Loaded' && trackingDetails.value) {
            const allData: (TrackingItem | MilestoneItem)[] = [];

            trackingDetails.value.forEach((item: ShipmentTrackingDetail, index) => {
                const trackingNumber = `tracking-${index}`;
                const isExpanded = expandedRows.has(trackingNumber);

                const trackingItem: TrackingItem = {
                    id: trackingNumber,
                    carrierName: item.carrierName || item.scac || UNKNOWN_VALUE,
                    carrierTrackingNumber: item.trackingNum || UNKNOWN_VALUE,
                    status: item.shipmentStatus || t('submittedStatus-label'),
                    estimatedDeliveryDate: item.estimatedDeliveryDate
                        ? new Date(item.estimatedDeliveryDate).toLocaleString()
                        : UNKNOWN_VALUE,
                    milestones: item.milestones || [],
                    isExpanded: isExpanded,
                };

                allData.push(trackingItem);

                if (isExpanded && item.milestones && item.milestones.length > 0) {
                    const latestMilestone = item.milestones.reduce((latest, current) => {
                        const latestTime = latest?.eventTime ?? 0;
                        const currentTime = current?.eventTime ?? 0;
                        return currentTime > latestTime ? current : latest;
                    }, item.milestones[0]);

                    const milestoneItem: MilestoneItem = {
                        id: `milestone-${trackingNumber}-latest`,
                        parentId: trackingNumber,
                        carrierTrackingNumber: `${
                            item.trackingNum || UNKNOWN_VALUE
                        }-${trackingNumber}-milestone-latest`,
                        status: latestMilestone.transitState || UNKNOWN_VALUE,
                        eventTime: latestMilestone.eventTime
                            ? new Date(latestMilestone.eventTime * 1000).toLocaleString()
                            : UNKNOWN_VALUE,
                        eventLocation: latestMilestone.eventAddress?.join(', ') || UNKNOWN_VALUE,
                        isMilestone: true,
                    };
                    allData.push(milestoneItem);
                }
            });

            return allData;
        }
        return [];
    }, [trackingDetails, expandedRows, UNKNOWN_VALUE, t]);

    const dynamicPageSize = useMemo(() => {
        if (trackingDetails.status === 'Loaded' && trackingDetails.value) {
            const totalShipments = trackingDetails.value.length;
            const totalPossibleMilestones = trackingDetails.value.reduce((total, item) => {
                return total + (item.milestones && item.milestones.length > 0 ? 1 : 0);
            }, 0);

            return totalShipments + totalPossibleMilestones;
        }
        return 10;
    }, [trackingDetails]);

    const columns: SuperPaginatedTableColumn[] = [
        {
            sourceProperty: 'carrierTrackingNumber',
            title: t('carrierTrackingNumber-columnHeader'),
            format: {
                render: (value: any, row: any) => {
                    if (row.isMilestone) {
                        return <MilestoneField label={t('eventTime-label')} value={row.eventTime} />;
                    }

                    const hasMillestones = row.milestones && row.milestones.length > 0;

                    if (hasMillestones) {
                        const expandIcon = row.isExpanded ? COLLAPSE_ICON : EXPAND_ICON;
                        return (
                            <span
                                // eslint-disable-next-line react/jsx-curly-brace-presence
                                role="button"
                                tabIndex={0}
                                style={{ cursor: 'pointer' }}
                                onClick={() => toggleRow(row.id)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        toggleRow(row.id);
                                    }
                                }}
                            >
                                {expandIcon}
                                {value}
                            </span>
                        );
                    }

                    return <span>{value}</span>;
                },
                sort: (value: any, row: any) => {
                    if (row.isMilestone) {
                        const parentIndex = row.parentId.split('-')[1];
                        return `${parentIndex}-milestone`;
                    }
                    const trackingIndex = row.id.split('-')[1];
                    return `${trackingIndex}-main`;
                },
                search: (value: any, row: any) => {
                    if (row.isMilestone) {
                        return `milestone ${row.eventTime || ''}`;
                    }
                    return String(value || '');
                },
            },
        },
        {
            sourceProperty: 'carrierName',
            title: t('carrierName-columnHeader'),
            format: {
                render: (value: any, row: any) => {
                    if (row.isMilestone) {
                        return '';
                    }
                    return value;
                },
                sort: (value: any, row: any) => {
                    if (row.isMilestone) {
                        const parentIndex = row.parentId.split('-')[1];
                        return `${parentIndex}-milestone`;
                    }
                    const trackingIndex = row.id.split('-')[1];
                    return `${trackingIndex}-main`;
                },
                search: (value: any, row: any) => {
                    if (row.isMilestone) {
                        return `milestone ${row.status || ''}`;
                    }
                    return String(value || '');
                },
            },
        },
        {
            sourceProperty: 'status',
            title: t('status-columnHeader'),
            format: {
                render: (value: any, row: any) => {
                    if (row.isMilestone) {
                        return <MilestoneField label={t('milestoneStatus-label')} value={row.status} />;
                    }
                    return value;
                },
                sort: (value: any, row: any) => {
                    if (row.isMilestone) {
                        const parentIndex = row.parentId.split('-')[1];
                        return `${parentIndex}-milestone`;
                    }
                    const trackingIndex = row.id.split('-')[1];
                    return `${trackingIndex}-main`;
                },
                search: (value: any, row: any) => {
                    if (row.isMilestone) {
                        return `milestone ${row.status || ''}`;
                    }
                    return String(value || '');
                },
            },
        },
        {
            sourceProperty: 'estimatedDeliveryDate',
            title: t('estimatedDeliveryDate-columnHeader'),
            format: {
                render: (value: any, row: any) => {
                    if (row.isMilestone) {
                        return '';
                    }
                    return value;
                },
                sort: (value: any, row: any) => {
                    if (row.isMilestone) {
                        const parentIndex = row.parentId.split('-')[1];
                        return `${parentIndex}-milestone`;
                    }
                    const trackingIndex = row.id.split('-')[1];
                    return `${trackingIndex}-main`;
                },
                search: (value: any, row: any) => {
                    if (row.isMilestone) {
                        return 'milestone';
                    }
                    return String(value || '');
                },
            },
        },
    ];

    return (
        <BorderedColumn alignmentHorizontal={'center'} spacing={'none'}>
            <SuperPaginatedTable
                data={formattedTrackingData}
                layout={{
                    pageSize: dynamicPageSize,
                    searchPlaceholder: t('findTracking-searchPlaceholder'),
                    columns,
                }}
            />
        </BorderedColumn>
    );
};

export { ReturnOrderTrackingTable };
